import {useCallback} from 'react';

/**
 * Hook for managing column selection state in localStorage
 * @returns {Object} Methods for working with column selection storage
 */
export const useColumnSelectorStorage = () => {
  const STORAGE_KEY_PREFIX = 'feedest_table_columns_';

  /**
   * Saves visible columns for a table to localStorage
   * @param {string} tableId - Unique identifier for the table
   * @param {string[]} columnIds - Array of column IDs that should be visible
   */
  const saveVisibleColumns = useCallback((tableId, columnIds) => {
    if (!tableId) return;

    try {
      const storageKey = `${STORAGE_KEY_PREFIX}${tableId}`;
      localStorage.setItem(storageKey, JSON.stringify(columnIds));
    } catch (err) {
      console.error('Error saving column selection to localStorage:', err);
    }
  }, []);

  /**
   * Gets visible columns for a table from localStorage
   * @param {string} tableId - Unique identifier for the table
   * @returns {string[]|null} Array of column IDs that should be visible or null if not found
   */
  const getVisibleColumns = useCallback((tableId) => {
    if (!tableId) return null;

    try {
      const storageKey = `${STORAGE_KEY_PREFIX}${tableId}`;
      const storedValue = localStorage.getItem(storageKey);

      if (storedValue) {
        return JSON.parse(storedValue);
      }
    } catch (err) {
      console.error('Error getting column selection from localStorage:', err);
    }

    return null;
  }, []);

  /**
   * Clears column selection for a specific table
   * @param {string} tableId - Unique identifier for the table
   */
  const clearVisibleColumns = useCallback((tableId) => {
    if (!tableId) return;

    try {
      const storageKey = `${STORAGE_KEY_PREFIX}${tableId}`;
      localStorage.removeItem(storageKey);
    } catch (err) {
      console.error('Error clearing column selection from localStorage:', err);
    }
  }, []);

  return {
    saveVisibleColumns,
    getVisibleColumns,
    clearVisibleColumns
  };
};
